import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import type { Project } from '../data/projectTypes'
import { useGetSecurityOwnershipQuery, useGetSecuritySetupQuery } from '../services/projectDataApi'
import { ownershipStatusLabel } from '../schemas/securityOwnership'
import { SecurityNonceSetup } from './chat/SecurityNonceChatCard'
import { Chip } from './ProjectSettingRows'
import './ProjectSettings.css'

export default function SecuritySettings({ project, canEdit, patch }: {
  project: Project
  canEdit: boolean
  patch: (body: Record<string, unknown>) => Promise<string | null>
}) {
  const [open, setOpen] = useState(false)
  const [enabling, setEnabling] = useState(false)
  const [enableError, setEnableError] = useState('')
  const exempt = project.security_ownership_exempt === true
  const configured = project.enabled_polish_passes.includes('security')
  const setup = useGetSecuritySetupQuery(project.id, { skip: !canEdit || !configured || !!project.security_nonce || exempt })
  const nonce = project.security_nonce || setup.currentData?.nonce || ''

  const ownership = useGetSecurityOwnershipQuery({ projectId: project.id, targetUrl: project.target_url ?? '', nonce }, {
    skip: !configured || exempt || !nonce,
    refetchOnMountOrArgChange: true,
  })
  const latest = ownership.currentData?.latest
  const confirmed = !ownership.isError && latest?.verified === true
  const ownershipLabel = ownershipStatusLabel(latest, ownership.isFetching, ownership.isError)

  if (!configured) {
    return (
      <div className="settings-row settings-row--stacked" data-testid="settings-row-security">
        <div className="settings-row__info">
          <span className="settings-row__label">Security testing</span>
          <span className="settings-row__detail">
            Analyze recorded traffic for security bugs. Confirm ownership to view findings and enable pentesting.
          </span>
        </div>
        <div className="settings-row__status">
          <Chip tone="muted">Off</Chip>
          {canEdit && (
            <button
              type="button"
              className="settings-row__change"
              disabled={enabling}
              onClick={() => {
                setEnabling(true)
                setEnableError('')
                void patch({
                  enabled_polish_passes: [...new Set([...(project.enabled_polish_passes ?? []), 'security'])],
                  security_probing_enabled: true,
                }).then((err) => {
                  setEnabling(false)
                  if (err) setEnableError(err)
                  else setOpen(true)
                })
              }}
              data-testid="settings-row-security-enable"
              data-analytics-event="project-settings-security-enable"
            >
              <Sparkles size={13} aria-hidden /> {enabling ? 'Setting up…' : 'Set up'}
            </button>
          )}
        </div>
        {enableError && <div className="settings-row__panel settings-row__error">{enableError}</div>}
      </div>
    )
  }

  if (exempt) {
    return (
      <div className="settings-row" data-testid="settings-row-security">
        <div className="settings-row__info">
          <span className="settings-row__label">Security testing</span>
          <span className="settings-row__detail">
            Your account is approved for security testing, so no ownership file is needed.
          </span>
        </div>
        <div className="settings-row__status"><Chip tone="ok">On</Chip></div>
      </div>
    )
  }

  return (
    <div className="settings-row settings-row--stacked" data-testid="settings-row-security">
      <div className="settings-row__info">
        <span className="settings-row__label">Security testing</span>
        <span className="settings-row__detail">
          Security scanning is enabled. {latest && !ownership.isError
            ? `Last checked ${new Date(latest.checked_at).toLocaleString()}. Re-checked before pentesting.`
            : 'Confirm ownership to view security findings and complete testing.'}
        </span>
      </div>
      <div className="settings-row__status">
        <Chip tone={confirmed ? 'ok' : 'muted'}>{ownershipLabel}</Chip>
        <button
          type="button"
          className="settings-row__change"
          onClick={() => setOpen((v) => !v)}
          data-testid="settings-row-security-toggle"
          data-analytics-event="project-settings-security-open"
        >
          {open ? 'Hide' : 'Show setup'}
        </button>
      </div>
      {enableError && <p className="settings-row__error" role="alert">{enableError}</p>}
      {open && (
        <div className="settings-row__panel">
          {/* Inline rather than the chat's dialog: settings is itself sometimes a modal, and the user
              already clicked a disclosure to ask for this. */}
          {setup.isLoading && <p role="status">Preparing ownership instructions…</p>}
          {setup.isError && <p role="alert">Could not prepare ownership instructions. <button onClick={() => void setup.refetch()}>Retry</button></p>}
          {nonce && <SecurityNonceSetup
            key={`${project.id}:${project.target_url}:${nonce}`}
            projectId={project.id}
            targetUrl={project.target_url ?? ''}
            nonce={nonce}
            verified={confirmed}
            onResult={async state => {
              if (!canEdit || !state.verified || project.security_probing_enabled) return
              setEnableError('')
              try {
                const error = await patch({ security_probing_enabled: true })
                if (error) setEnableError(error)
              } catch { setEnableError('Ownership confirmed, but pentesting could not start. Check again to retry.') }
            }}
          />}
        </div>
      )}
    </div>
  )
}

